<template>
  <div class="bg-gradient-to-b from-slate-700 to-slate-900 p-4 md:p-8 z-0">
    <!-- Enhanced Hero Section -->
    <div class="relative overflow-hidden rounded-2xl mb-20 bg-gradient-to-br from-purple-900 via-slate-900 to-slate-800">
      <div class="absolute inset-0">
        <!-- Decorative elements -->
        <div class="absolute top-0 left-0 w-full h-full overflow-hidden">
          <div class="absolute -top-24 -left-24 w-72 h-72 bg-purple-500 rounded-full mix-blend-multiply filter blur-2xl opacity-20 animate-blob"></div>
          <div class="absolute -bottom-24 -right-24 w-72 h-72 bg-blue-500 rounded-full mix-blend-multiply filter blur-2xl opacity-20 animate-blob animation-delay-2000"></div>
          <div class="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-72 h-72 bg-pink-500 rounded-full mix-blend-multiply filter blur-2xl opacity-20 animate-blob animation-delay-4000"></div>
        </div>
      </div>

      <div class="relative px-8 py-16 md:py-24 max-w-4xl mx-auto">
        <div class="grid md:grid-cols-2 gap-8 items-center">
          <div class="text-left">
            <h1 class="text-4xl md:text-6xl font-bold text-white mb-6 leading-tight">
              Research Papers,
              <span class="text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-pink-400">Simplified</span>
            </h1>
            <p class="text-xl text-gray-300 mb-8">Your AI-powered companion for understanding complex research papers with ease.</p>
            <router-link 
              to="/signup" 
              class="inline-flex items-center px-8 py-4 bg-gradient-to-r from-purple-500 to-purple-700 text-white rounded-lg font-semibold hover:from-purple-600 hover:to-purple-800 transition-all duration-200 shadow-lg hover:shadow-purple-500/25"
            >
              Get Started For Free
              <i class="pi pi-arrow-right ml-2"></i>
            </router-link>
          </div>
          
          <!-- Decorative Icons Grid -->
          <div class="hidden md:grid grid-cols-2 gap-4 relative">
            <div class="absolute inset-0 bg-gradient-to-br from-purple-500/10 to-transparent rounded-lg filter blur-xl"></div>
            <div class="p-6 bg-white/10 rounded-lg backdrop-blur-sm">
              <i class="pi pi-file-pdf text-4xl text-purple-400"></i>
            </div>
            <div class="p-6 bg-white/10 rounded-lg backdrop-blur-sm">
              <i class="pi pi-book text-4xl text-blue-400"></i>
            </div>
            <div class="p-6 bg-white/10 rounded-lg backdrop-blur-sm">
              <i class="pi pi-chart-line text-4xl text-pink-400"></i>
            </div>
            <div class="p-6 bg-white/10 rounded-lg backdrop-blur-sm">
              <i class="pi pi-bookmark text-4xl text-purple-400"></i>
            </div>
          </div>
        </div>

        <!-- Value Proposition Section -->
        <div class="grid grid-cols-2 md:grid-cols-3 gap-4 mt-16">
          <div class="text-center p-4 bg-white/5 rounded-lg backdrop-blur-sm">
            <div class="text-3xl mb-2">
              <i class="pi pi-bolt text-yellow-400"></i>
              <i class="pi pi-clock text-purple-400 ml-1"></i>
            </div>
            <div class="text-gray-300">
              <span class="block font-semibold mb-1">Save Time</span>
              Get key insights quickly with AI-powered summaries
            </div>
          </div>
          
          <div class="text-center p-4 bg-white/5 rounded-lg backdrop-blur-sm">
            <div class="text-3xl mb-2">
              <i class="pi pi-star-fill text-yellow-400"></i>
              <i class="pi pi-heart-fill text-pink-400 ml-1"></i>
            </div>
            <div class="text-gray-300">
              <span class="block font-semibold mb-1">Active Learning</span>
              Research-backed techniques for better comprehension
            </div>
          </div>
          
          <div class="text-center p-4 bg-white/5 rounded-lg backdrop-blur-sm">
            <div class="text-3xl mb-2">
              <i class="pi pi-compass text-cyan-400"></i>
              <i class="pi pi-chart-line text-green-400 ml-1"></i>
            </div>
            <div class="text-gray-300">
              <span class="block font-semibold mb-1">Deep Understanding</span>
              Progressive reading system for thorough analysis
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Rest of the sections remain the same -->
    <div class="max-w-4xl mx-auto">
      <!-- Paper Import Methods -->
      <section class="mb-16">
        <h2 class="text-2xl md:text-3xl font-semibold text-white mb-6">Multiple Ways to Start</h2>
        <div class="grid md:grid-cols-2 gap-6">
          <div class="bg-white/10 rounded-lg p-6 backdrop-blur-sm">
            <div class="flex items-start mb-4">
              <i class="pi pi-file-pdf text-2xl text-purple-400 mr-4"></i>
              <div>
                <h3 class="text-xl font-semibold text-white mb-2">PDF Upload</h3>
                <p class="text-gray-300">Upload research papers directly from your device. Supports papers up to 10MB with automatic OCR for scanned documents.</p>
              </div>
            </div>
          </div>

          <div class="bg-white/10 rounded-lg p-6 backdrop-blur-sm">
            <div class="flex items-start mb-4">
              <i class="pi pi-search text-2xl text-purple-400 mr-4"></i>
              <div>
                <h3 class="text-xl font-semibold text-white mb-2">arXiv Integration</h3>
                <p class="text-gray-300">Search and access papers directly from arXiv's extensive database of scientific papers and preprints.</p>
              </div>
            </div>
          </div>

          <div class="bg-white/10 rounded-lg p-6 backdrop-blur-sm">
            <div class="flex items-start mb-4">
              <i class="pi pi-book text-2xl text-purple-400 mr-4"></i>
              <div>
                <h3 class="text-xl font-semibold text-white mb-2">Google Scholar</h3>
                <p class="text-gray-300">Find and analyze papers from Google Scholar's comprehensive collection of academic literature.</p>
              </div>
            </div>
          </div>

          <div class="bg-white/10 rounded-lg p-6 backdrop-blur-sm">
            <div class="flex items-start mb-4">
              <i class="pi pi-link text-2xl text-purple-400 mr-4"></i>
              <div>
                <h3 class="text-xl font-semibold text-white mb-2">Direct URL</h3>
                <p class="text-gray-300">Simply paste a direct link to any accessible research paper PDF to start analyzing.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- Key Features -->
      <section class="mb-16">
        <div class="flex items-center gap-4 mb-6">
          <h2 class="text-2xl md:text-3xl font-semibold text-white">Advanced Features</h2>
          <span class="px-4 py-2 bg-purple-500/20 rounded-full text-purple-300 text-sm font-medium">
            Coming Soon! 🚀
          </span>
        </div>
        <div class="grid md:grid-cols-3 gap-6">
          <div class="bg-white/10 rounded-lg p-6 backdrop-blur-sm">
            <div class="flex items-center mb-4">
              <div class="w-12 h-12 rounded-lg bg-purple-500/10 flex items-center justify-center mr-4">
                <i class="pi pi-clone text-2xl text-purple-400"></i>
              </div>
              <h3 class="text-xl font-semibold text-white">Multi-Paper Analysis</h3>
            </div>
            <p class="text-gray-300 mb-4">Compare and analyze multiple papers simultaneously to identify connections, contradictions, and complementary findings.</p>
            <ul class="text-gray-300 space-y-2">
              <li class="flex items-center">
                <i class="pi pi-check text-green-400 mr-2"></i>
                Cross-reference findings
              </li>
              <li class="flex items-center">
                <i class="pi pi-check text-green-400 mr-2"></i>
                Compare methodologies
              </li>
              <li class="flex items-center">
                <i class="pi pi-check text-green-400 mr-2"></i>
                Track research evolution
              </li>
            </ul>
          </div>

          <div class="bg-white/10 rounded-lg p-6 backdrop-blur-sm">
            <div class="flex items-center mb-4">
              <div class="w-12 h-12 rounded-lg bg-blue-500/10 flex items-center justify-center mr-4">
                <i class="pi pi-file-edit text-2xl text-blue-400"></i>
              </div>
              <h3 class="text-xl font-semibold text-white">Smart Reports</h3>
            </div>
            <p class="text-gray-300 mb-4">Generate comprehensive reports tailored to your needs:</p>
            <ul class="text-gray-300 space-y-2">
              <li class="flex items-center">
                <i class="pi pi-check text-green-400 mr-2"></i>
                Executive summaries
              </li>
              <li class="flex items-center">
                <i class="pi pi-check text-green-400 mr-2"></i>
                Literature reviews
              </li>
              <li class="flex items-center">
                <i class="pi pi-check text-green-400 mr-2"></i>
                Research gap analysis
              </li>
              <li class="flex items-center">
                <i class="pi pi-check text-green-400 mr-2"></i>
                Citation suggestions
              </li>
            </ul>
          </div>

          <div class="bg-white/10 rounded-lg p-6 backdrop-blur-sm">
            <div class="flex items-center mb-4">
              <div class="w-12 h-12 rounded-lg bg-pink-500/10 flex items-center justify-center mr-4">
                <i class="pi pi-book text-2xl text-pink-400"></i>
              </div>
              <h3 class="text-xl font-semibold text-white">Progressive Reading</h3>
            </div>
            <p class="text-gray-300 mb-4">Three-tier reading system:</p>
            <ul class="text-gray-300 space-y-2">
              <li class="flex items-center">
                <i class="pi pi-check text-green-400 mr-2"></i>
                1st Read: Quick overview
              </li>
              <li class="flex items-center">
                <i class="pi pi-check text-green-400 mr-2"></i>
                2nd Read: Detailed analysis
              </li>
              <li class="flex items-center">
                <i class="pi pi-check text-green-400 mr-2"></i>
                3rd Read: Critical evaluation
              </li>
            </ul>
          </div>
        </div>
      </section>

      <!-- Study Tools -->
      <section class="mb-16">
        <div class="flex items-center gap-4 mb-6">
          <h2 class="text-2xl md:text-3xl font-semibold text-white">Study Tools</h2>
          <span class="px-4 py-2 bg-purple-500/20 rounded-full text-purple-300 text-sm font-medium">
            Coming Soon! 📚
          </span>
        </div>
        <div class="grid md:grid-cols-2 gap-6">
          <div class="bg-white/10 rounded-lg p-6 backdrop-blur-sm">
            <div class="flex items-center mb-4">
              <div class="w-12 h-12 rounded-lg bg-purple-500/10 flex items-center justify-center mr-4">
                <i class="pi pi-copy text-2xl text-purple-400"></i>
              </div>
              <h3 class="text-xl font-semibold text-white">Smart Flashcards</h3>
            </div>
            <p class="text-gray-300 mb-4">Automatically generate study cards from papers:</p>
            <ul class="text-gray-300 space-y-2">
              <li class="flex items-center">
                <i class="pi pi-check text-green-400 mr-2"></i>
                Key concepts
              </li>
              <li class="flex items-center">
                <i class="pi pi-check text-green-400 mr-2"></i>
                Important findings
              </li>
              <li class="flex items-center">
                <i class="pi pi-check text-green-400 mr-2"></i>
                Methodologies
              </li>
              <li class="flex items-center">
                <i class="pi pi-check text-green-400 mr-2"></i>
                Spaced repetition support
              </li>
            </ul>
          </div>

          <div class="bg-white/10 rounded-lg p-6 backdrop-blur-sm">
            <div class="flex items-center mb-4">
              <div class="w-12 h-12 rounded-lg bg-blue-500/10 flex items-center justify-center mr-4">
                <i class="pi pi-pencil text-2xl text-blue-400"></i>
              </div>
              <h3 class="text-xl font-semibold text-white">AI Notepad</h3>
            </div>
            <p class="text-gray-300 mb-4">Smart note-taking features:</p>
            <ul class="text-gray-300 space-y-2">
              <li class="flex items-center">
                <i class="pi pi-check text-green-400 mr-2"></i>
                Auto-summarization
              </li>
              <li class="flex items-center">
                <i class="pi pi-check text-green-400 mr-2"></i>
                Citation management
              </li>
              <li class="flex items-center">
                <i class="pi pi-check text-green-400 mr-2"></i>
                Concept linking
              </li>
              <li class="flex items-center">
                <i class="pi pi-check text-green-400 mr-2"></i>
                Export capabilities
              </li>
            </ul>
          </div>
        </div>
      </section>

      <!-- Call to Action -->
      <div class="text-center">
        <router-link 
          to="/" 
          class="inline-block px-8 py-4 bg-gradient-to-r from-purple-500 to-purple-700 text-white rounded-lg font-semibold hover:from-purple-600 hover:to-purple-800 transition-all duration-200"
        >
          Start Reading Now
        </router-link>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'AboutPage'
}
</script>

<style scoped>
.animate-blob {
  animation: blob 7s infinite;
}

.animation-delay-2000 {
  animation-delay: 2s;
}

.animation-delay-4000 {
  animation-delay: 4s;
}

@keyframes blob {
  0% {
    transform: translate(0px, 0px) scale(1);
  }
  33% {
    transform: translate(30px, -50px) scale(1.1);
  }
  66% {
    transform: translate(-20px, 20px) scale(0.9);
  }
  100% {
    transform: translate(0px, 0px) scale(1);
  }
}
</style> 